/**
 * @fileoverview NotificationsContext
 *
 * Estado global de la inbox del usuario autenticado. Combina:
 *   - Hidratación inicial (`/notifications/me?limit=20`).
 *   - Stream SSE en tiempo real (`/notifications/me/events`) para
 *     actualizar items + badge sin polling.
 *   - Toast on-screen cuando llega una nueva notificación, con click
 *     que navega al deepLink.
 *   - Operaciones optimistas: mark-as-read, mark-all-read.
 *
 * El bell del header consume este contexto en lugar de tener su
 * propio polling — evitamos múltiples requests duplicadas y
 * mantenemos la UI sincronizada entre tabs (cada tab abre su propio
 * SSE; el backend emite a todas).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  notificationService,
  type InAppNotification,
} from '@/services/notification.service';
import { useAuth } from './AuthContext';
import { useEventBus } from './EventBusContext';
import { resolveDashboardLink } from '@/lib/deepLink';

// ─── Wire shapes (payloads emitted by the unified bus) ──────────────

interface NotificationCreatedData {
  type: 'notification.created';
  id: string;
  title: string;
  body: string;
  category: string;
  priority: string;
  deepLink: string | null;
  referenceId: string | null;
  referenceType: string | null;
  imageUrl: string | null;
  createdAt: string;
}
interface NotificationReadData {
  type: 'notification.read';
  id: string;
}
interface NotificationDeletedData {
  type: 'notification.deleted';
  id: string;
}

/**
 * Último evento `notification.created` recibido por SSE. Lo expone el
 * provider para que páginas/modales abiertos reaccionen a eventos
 * específicos de su entidad sin tener que hacer polling.
 */
export interface NotificationLatestEvent {
  notificationId: string;
  referenceId: string | null;
  referenceType: string | null;
  category: string;
  receivedAt: number;
}

interface NotificationsContextValue {
  items: InAppNotification[];
  unread: number;
  isLoading: boolean;
  latestEvent: NotificationLatestEvent | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /**
   * Atajo para handlers de click en bell/inbox: marca leída
   * optimisticamente y navega al deepLink (forzando re-trigger
   * incluso cuando ya estamos en la misma ruta — la búsqueda
   * de query params abre el detalle correspondiente).
   */
  openNotification: (n: InAppNotification) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const INBOX_LIMIT = 20;

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { subscribe } = useEventBus();
  const navigate = useNavigate();

  const [items, setItems] = useState<InAppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [latestEvent, setLatestEvent] = useState<NotificationLatestEvent | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  /**
   * Espejo de `items` para saber, al recibir un evento, si la notificación
   * ya estaba leída. El backend emite `notification.read` a todas las
   * pestañas —también a la que la marcó—, así que restar a ciegas contaba
   * dos veces la misma lectura y el contador acababa por debajo de la
   * realidad (o en 0 con notificaciones pendientes).
   */
  const itemsRef = useRef<InAppNotification[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  /**
   * ¿Contaba como no leída? Si no está en la bandeja (más allá de las 20
   * cargadas) se confía en el evento: el backend sólo lo emite en la
   * transición a leída.
   */
  const wasUnread = useCallback((id: string): boolean => {
    const known = itemsRef.current.find((it) => it.id === id);
    return known ? !known.readAt : true;
  }, []);

  /** Marca leída en local y resta una sola vez por notificación. */
  const applyRead = useCallback(
    (id: string) => {
      const counted = wasUnread(id);
      const readAt = new Date().toISOString();
      const next = itemsRef.current.map((it) =>
        it.id === id && !it.readAt ? { ...it, readAt } : it,
      );
      itemsRef.current = next;
      setItems(next);
      if (counted) setUnread((u) => Math.max(0, u - 1));
    },
    [wasUnread],
  );

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    ctrlRef.current?.abort();
    const c = new AbortController();
    ctrlRef.current = c;
    setIsLoading(true);
    try {
      const res = await notificationService.listMine(
        { limit: INBOX_LIMIT },
        { signal: c.signal },
      );
      if (c.signal.aborted) return;
      setItems(res.data);
      setUnread(res.meta.unread);
    } catch {
      /* silent — bell stays at last state */
    } finally {
      if (!c.signal.aborted) setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Hidratación + suscripción al bus unificado. El bus mantiene UNA
  // conexión SSE para todo el dashboard (`/events/stream`); este
  // contexto solo declara qué tipos de evento le interesan.
  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setUnread(0);
      return;
    }

    refresh();

    const unsubs: Array<() => void> = [];

    unsubs.push(
      subscribe<NotificationCreatedData>('notification.created', (event) => {
        const incoming: InAppNotification = {
          id: event.id,
          channel: 'IN_APP',
          priority: event.priority,
          status: 'DELIVERED',
          title: event.title,
          body: event.body,
          imageUrl: event.imageUrl,
          deepLink: event.deepLink,
          category: event.category,
          referenceId: event.referenceId,
          referenceType: event.referenceType,
          readAt: null,
          createdAt: event.createdAt,
        };
        // Un `created` de una notificación que ya teníamos sin leer (eco o
        // reenvío) la reemplaza, pero no es una pendiente más.
        const previous = itemsRef.current.find((it) => it.id === incoming.id);
        const next = previous
          ? itemsRef.current.map((it) => (it.id === incoming.id ? incoming : it))
          : [incoming, ...itemsRef.current].slice(0, INBOX_LIMIT);
        itemsRef.current = next;
        setItems(next);
        if (!previous || previous.readAt) setUnread((u) => u + 1);
        setLatestEvent({
          notificationId: event.id,
          referenceId: event.referenceId,
          referenceType: event.referenceType,
          category: event.category,
          receivedAt: Date.now(),
        });
        toast(event.title, {
          description: event.body,
          duration: 6000,
          action: resolveDashboardLink(event.deepLink)
            ? {
                label: 'Ver',
                onClick: () => navigateToDeepLink(event.deepLink!),
              }
            : undefined,
        });
      }),
    );

    unsubs.push(
      subscribe<NotificationReadData>('notification.read', (event) => {
        applyRead(event.id);
      }),
    );

    unsubs.push(
      subscribe('notification.read-all', () => {
        setItems((prev) =>
          prev.map((it) =>
            it.readAt ? it : { ...it, readAt: new Date().toISOString() },
          ),
        );
        setUnread(0);
      }),
    );

    unsubs.push(
      subscribe<NotificationDeletedData>('notification.deleted', (event) => {
        // Borrar una ya leída no cambia las pendientes.
        const counted = wasUnread(event.id);
        const next = itemsRef.current.filter((it) => it.id !== event.id);
        itemsRef.current = next;
        setItems(next);
        if (counted) setUnread((u) => Math.max(0, u - 1));
      }),
    );

    return () => {
      for (const u of unsubs) u();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, refresh, subscribe, applyRead, wasUnread]);

  /**
   * Navegación robusta a un deepLink incluso si ya estamos en la
   * misma ruta. El truco: si el destino tiene query params los
   * dejamos pasar; React Router actualiza search y la página
   * destino reacciona al cambio en `useSearchParams`. Si el
   * destino no tiene query, agregamos `?n=<timestamp>` como
   * hint para forzar un cambio de location y evitar el no-op.
   */
  const navigateToDeepLink = useCallback(
    (deepLink: string) => {
      // `deepLink` es una columna compartida con la app móvil y los otros
      // paneles. Si la ruta no es de aquí, navegar sólo lleva al 404
      // propio. Ver `resolveDashboardLink`.
      const target = resolveDashboardLink(deepLink);
      if (!target) return;
      const hasQuery = target.includes('?');
      const url = hasQuery ? target : `${target}?n=${Date.now()}`;
      navigate(url);
    },
    [navigate],
  );

  const markRead = useCallback(async (id: string) => {
    applyRead(id);
    try {
      await notificationService.markRead(id);
    } catch {
      // El SSE traerá el estado real en el próximo evento. Si la
      // request falló por 401/network, refresh restaura.
    }
  }, [applyRead]);

  const markAllRead = useCallback(async () => {
    if (unread === 0) return;
    const previous = unread;
    setItems((prev) =>
      prev.map((it) => (it.readAt ? it : { ...it, readAt: new Date().toISOString() })),
    );
    setUnread(0);
    try {
      await notificationService.markAllRead();
    } catch {
      setUnread(previous);
    }
  }, [unread]);

  const openNotification = useCallback(
    (n: InAppNotification) => {
      if (!n.readAt) {
        void markRead(n.id);
      }
      if (n.deepLink) {
        navigateToDeepLink(n.deepLink);
      }
    },
    [markRead, navigateToDeepLink],
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      items,
      unread,
      isLoading,
      latestEvent,
      refresh,
      markRead,
      markAllRead,
      openNotification,
    }),
    [
      items,
      unread,
      isLoading,
      latestEvent,
      refresh,
      markRead,
      markAllRead,
      openNotification,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
}
